import React, { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { Tooltip } from 'react-tooltip';
import { Approach } from '../../types';

const ClampedTextWithTooltip: React.FC<{
  id: string;
  text: string;
  className?: string;
}> = ({ id, text, className }) => {
  const elRef = useRef<HTMLSpanElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const measure = () => {
      const el = elRef.current;
      if (!el) return;
      const truncated = el.scrollHeight > el.clientHeight + 1;
      setIsTruncated(truncated);
    };

    measure();
    window.addEventListener('resize', measure);

    const t = window.setTimeout(measure, 0);
    return () => {
      window.clearTimeout(t);
      window.removeEventListener('resize', measure);
    };
  }, [text]);

  return (
    <>
      <span
        ref={elRef}
        tabIndex={0}
        {...(isTruncated
          ? {
              'data-tooltip-id': id,
              'data-tooltip-content': text,
            }
          : {})}
        className={`overflow-hidden line-clamp-2 outline-none ${className ?? ''}`}
      >
        {text}
      </span>
      {isTruncated && (
        <Tooltip
          id={id}
          place="bottom-start"
          className="!bg-white !text-gray-800 !border !border-blue-100 !shadow-lg !opacity-100"
          style={{ maxWidth: '320px' }}
        />
      )}
    </>
  );
};

interface ApproachesListProps {
  approaches: Approach[];
  currentApproach: number;
  onSelectApproach: (index: number) => void;
}

export const ApproachesList: React.FC<ApproachesListProps> = ({
  approaches,
  currentApproach,
  onSelectApproach,
}) => {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-3 flex-1 flex flex-col overflow-hidden min-h-0">
      <h2 className="text-sm font-semibold mb-3 text-gray-900">Approaches</h2>
      <div className="space-y-2 flex-1 overflow-y-auto min-h-0">
        {approaches.map((approach, index) => (
          <button
            key={index}
            onClick={() => onSelectApproach(index)}
            className={`w-full text-left p-2 rounded-r-lg transition-all duration-200 hover:bg-gray-50 border-l-4 ${
              currentApproach === index ? 'border-blue-500 bg-blue-50' : 'border-transparent'
            }`}
          >
            <div className="flex items-center gap-2">
              <div
                className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-medium ${
                  currentApproach === index ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600'
                }`}
              >
                {currentApproach === index ? <Check className="w-3 h-3" /> : index + 1}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-medium text-gray-800 truncate">
                  {approach.title}
                </div>
                {currentApproach === index && approach.description?.trim() && (
                  <ClampedTextWithTooltip
                    id={`approach-desc-${index}`}
                    text={approach.description}
                    className="mt-1 block text-[11px] text-gray-600 leading-snug"
                  />
                )}
                {currentApproach === index && approach.commonMistake?.trim() && (
                  <div className="mt-1 text-[11px] text-red-600 leading-snug max-h-10">
                    {approach.commonMistake}
                  </div>
                )}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default ApproachesList;
